import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, TableScroll } from '@mlm/ui';
import { getLedger, getWallet } from '../lib/api';
import { useHouseMoneyHidden } from '../lib/use-house-money';
import { QueryError } from '../components/query-error';

/** Ledger kinds in words. Older refund rows were written as a plain adjustment. */
const KIND_LABEL: Record<string, string> = {
  sale_earn: 'Course sale',
  refund_reversal: 'Refund',
  payout_hold: 'Cash-out',
  payout_refund: 'Cash-out returned',
  adjustment: 'Adjustment',
};
function kindLabel(kind: string, metadata: Record<string, unknown> | null | undefined): string {
  if (kind === 'adjustment' && metadata?.reason === 'refund_reversal') return 'Refund';
  return KIND_LABEL[kind] ?? kind.replace(/_/g, ' ');
}

export function WalletPage() {
  // A HOUSE creator has no wallet to show; the nav hides these entries, this
  // is the deep-link backstop.
  const hideMoney = useHouseMoneyHidden();
  const walletQ = useQuery({ queryKey: ['creator-wallet'], queryFn: getWallet });
  const ledgerQ = useQuery({
    queryKey: ['creator-ledger'],
    queryFn: () => getLedger(),
  });
  const wallet = walletQ.data;
  const ledger = ledgerQ.data ?? [];

  if (hideMoney) return <Navigate to="/" replace />;

  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-2xl font-semibold">Wallet</h1>
      {walletQ.isError && (
        <QueryError
          what="your balance"
          error={walletQ.error}
          onRetry={() => void walletQ.refetch()}
        />
      )}
      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${walletQ.isError ? 'hidden' : ''}`}>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Available</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold tabular-nums">
            ₱{Number(wallet?.available ?? 0).toFixed(2)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Lifetime earned</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold tabular-nums">
            ₱{Number(wallet?.lifetimeEarned ?? 0).toFixed(2)}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ledger</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {ledgerQ.isError ? (
            <div className="p-4">
              <QueryError
                what="your ledger"
                error={ledgerQ.error}
                onRetry={() => void ledgerQ.refetch()}
              />
            </div>
          ) : ledgerQ.isLoading ? (
            <p className="py-8 text-center text-muted-foreground text-sm">Loading…</p>
          ) : ledger.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">No entries yet.</p>
          ) : (
            <TableScroll>
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="p-3">Kind</th>
                    <th className="p-3">Order</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((r) => {
                    const b = r.metadata?.breakdown as
                      | { gross: string; fee: string; net: string }
                      | undefined;
                    return (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30 align-top">
                        <td className="p-3">{kindLabel(r.kind, r.metadata)}</td>
                        <td className="p-3 font-mono text-xs">{r.orderCode ?? '—'}</td>
                        <td
                          className={`p-3 tabular-nums ${r.direction === 'C' ? 'text-credit' : 'text-debit'}`}
                        >
                          <div>
                            {r.direction === 'C' ? '+' : '−'}₱{Number(r.amount).toFixed(2)}
                          </div>
                          {b && (
                            <div className="mt-0.5 text-[11px] font-normal text-muted-foreground">
                              ₱{Number(b.gross).toFixed(2)} sale − ₱{Number(b.fee).toFixed(2)}{' '}
                              platform fee
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {new Date(r.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
